import React, { forwardRef } from 'react';
import classNames from 'classnames';

import { showError, showToast } from 'legacy/common/utils/toast';
import { useUpdateRequestApprovalMutation } from 'legacy/events/hooks';
import { TierApproval } from 'legacy/events/types';

interface RequestListRowProps {
  eventId: string;
  request: TierApproval;
  onClick: () => void;
}

const RequestListRow = forwardRef<HTMLDivElement, RequestListRowProps>(
  ({ eventId, request, onClick }, ref) => {
    const updateRequestApprovalMutation = useUpdateRequestApprovalMutation();

    const onClickReject = (request: TierApproval) => {
      showToast({
        icon: '👎',
        message: `${request.user?.firstName} ${request.user?.lastName} was denied approval`
      });

      updateRequestApprovalMutation.mutate(
        {
          approvalId: request.id,
          eventId,
          status: 'rejected',
          readStatus: 'read'
        },
        {
          onError: (error) => {
            showError({ title: 'Error', message: error.message });
          }
        }
      );
    };

    const onClickApprove = (request: TierApproval) => {
      showToast({
        icon: '👍',
        message: `${request.user?.firstName} ${request.user?.lastName} was approved`
      });

      updateRequestApprovalMutation.mutate(
        {
          approvalId: request.id,
          eventId,
          status: 'approved',
          readStatus: 'read'
        },
        {
          onError: (error) => {
            showError({ title: 'Error', message: error.message });
          }
        }
      );
    };

    return (
      <div ref={ref} className="flex justify-between mb-6">
        <div className="flex items-start cursor-pointer" onClick={onClick}>
          <div className="shrink-0 mr-2 relative">
            <div className="border-2 border-primary-50 rounded-full flex items-center justify-center">
              <img
                src={request?.user?.photoUrl || '/img/search/user.svg'}
                className="w-10 h-10 rounded-full object-center"
              />
            </div>
            {request?.readStatus === 'unread' && (
              <div className="w-3 h-3 bg-primary-500 rounded-full absolute top-0 right-0 border-2 border-white" />
            )}
          </div>

          <div>
            <div
              className={classNames('text-sm', {
                'font-bold': request?.readStatus === 'unread'
              })}
            >
              {request?.user?.firstName} {request?.user?.lastName}
            </div>
            <p className="text-sm font-normal text-gray-500 pr-16">
              {request?.tier?.name}
            </p>
          </div>
        </div>

        <div className="flex items-center shrink-0 gap-2">
          <button
            type="button"
            className={classNames(
              'w-8 h-8 rounded-full p-1 border-1 border-primary-50 text-sm text-center',
              {
                'bg-gray-500': request.status === 'rejected'
              }
            )}
            onClick={() => onClickReject(request)}
          >
            👎
          </button>
          <button
            type="button"
            className={classNames(
              'w-8 h-8 rounded-full p-1 border-1 border-primary-50 text-sm text-center',
              {
                'bg-gray-500': request.status === 'approved'
              }
            )}
            onClick={() => onClickApprove(request)}
          >
            👍
          </button>
        </div>
      </div>
    );
  }
);

export default RequestListRow;
